import * as React from "react";
import { ChevronDown, Lock } from "lucide-react";
import { Popover, Tooltip } from "@/components/ui";
import { cn } from "@/lib/utils";

/**
 * The post's style settings (look, branding, background) as one bordered bar of equal cells. Cells
 * that don't apply aren't rendered, and the rest share the width.
 */
export const StyleBar: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div
    className="flex divide-x divide-slate-200 overflow-hidden rounded-xl border border-slate-200 bg-slate-50/70 shadow-xs"
    data-testid="style-row"
  >
    {children}
  </div>
);

export interface StyleCellProps {
  label: string;
  value: string;
  /** A small picture of the current choice (thumbnail, swatch, photo crop). */
  preview: React.ReactNode;
  disabled?: boolean;
  /** Why the setting can't be changed; shows a lock and explains it in a tooltip. */
  lockedReason?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Width and padding for the popover. */
  contentClassName?: string;
  /** The picker shown in a popover; without one the cell is a plain button that reports `onOpenChange(true)`. */
  children?: React.ReactNode;
  "data-testid"?: string;
}

/** One setting: a label over its current value with a small preview, opening its picker. */
export const StyleCell: React.FC<StyleCellProps> = ({
  label,
  value,
  preview,
  disabled,
  lockedReason,
  open,
  onOpenChange,
  contentClassName,
  children,
  "data-testid": testId,
}) => {
  const trigger = (
    <button
      type="button"
      aria-label={`${label}: ${value}`}
      title={value}
      data-testid={testId}
      className={cn(
        "group flex min-w-0 flex-1 items-center gap-2.5 px-3 py-2 text-left transition-colors cursor-pointer outline-none",
        "hover:bg-white focus-visible:bg-white focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/40",
        "disabled:cursor-default disabled:opacity-60 disabled:hover:bg-transparent",
        open && "bg-white"
      )}
    />
  );
  const content = (
    <>
      <span className="flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-md bg-white ring-1 ring-slate-200">
        {preview}
      </span>
      <span className="flex min-w-0 flex-col leading-tight">
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{label}</span>
        <span className="truncate text-sm font-semibold text-slate-800">{value}</span>
      </span>
      {lockedReason ? (
        <Lock className="ml-auto size-4 shrink-0 text-slate-400" aria-hidden="true" data-testid="style-cell-lock" />
      ) : (
        <ChevronDown
          className={cn(
            "ml-auto size-4 shrink-0 text-slate-400 transition-transform group-hover:text-slate-600",
            open && "rotate-180"
          )}
          aria-hidden="true"
        />
      )}
    </>
  );

  // A disabled button gets no pointer events, so the tooltip hangs off a wrapper that fills the cell.
  if (lockedReason) {
    return (
      <Tooltip>
        <Tooltip.Trigger render={<span className="flex min-w-0 flex-1" />}>
          {React.cloneElement(trigger, { disabled: true, "aria-description": lockedReason }, content)}
        </Tooltip.Trigger>
        <Tooltip.Content>{lockedReason}</Tooltip.Content>
      </Tooltip>
    );
  }

  if (children === undefined) {
    return React.cloneElement(trigger, { disabled, onClick: () => onOpenChange(true) }, content);
  }

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <Popover.Trigger disabled={disabled} render={trigger}>
        {content}
      </Popover.Trigger>
      <Popover.Content className={contentClassName ?? "w-72 p-2"} align="start">
        {children}
      </Popover.Content>
    </Popover>
  );
};
